/**
 * Faction slab / orb accent colors (same palette as OrderDesign minimized cards).
 * Single source for battle UI + action log name highlights.
 */

export const FACTION_SLAB_COLORS = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  "Ra's Children": '#F4A460',
  Asrah: '#8B0000',
  'The Sentients': '#ADD8E6',
  Combined: '#FFA500',
};

/** Strategy names that use the Combined accent (multi-faction decks). Keys only are used for lookup. */
export const COMBINED_FACTION_STRATEGIES = {
  'The Conspiracy': ['The Conspiracy', 'League of Shadows', 'Asrah', 'The Sentients'],
  'The Corrupted Core': ['The Corrupted Core', 'Exolot Dominion', 'Asrah', 'The Sentients'],
  'The Covenant': ['The Covenant', "Ra's Children", 'League of Shadows', 'The Sentients'],
  'The Divine Syndicate': ['The Divine Syndicate', "Ra's Children", 'Exolot Dominion', 'The Sentients'],
  'The Forsaken Pact': ['The Forsaken Pact', 'Remnants of Humanity', 'Asrah', 'The Sentients'],
  'The Hollow': ['The Hollow', "Ra's Children", 'Asrah', 'The Sentients'],
  'The Iron Alliance': ['The Iron Alliance', 'Remnants of Humanity', 'Exolot Dominion', 'The Sentients'],
  'The Last Crusade': ['The Last Crusade', "Ra's Children", 'Remnants of Humanity', 'The Sentients'],
  'The Shadow Consortium': ['The Shadow Consortium', 'League of Shadows', 'Exolot Dominion', 'The Sentients'],
  'The Silent Accord': ['The Silent Accord', 'Remnants of Humanity', 'League of Shadows', 'The Sentients'],
};

export function isCombinedStrategyFactionName(factionName) {
  if (!factionName || typeof factionName !== 'string') return false;
  return Object.prototype.hasOwnProperty.call(COMBINED_FACTION_STRATEGIES, factionName);
}

/**
 * Accent hex for log highlights / text (mirrors OrderDesign slab color rules, without React).
 * @param {object | null | undefined} entity order-like: faction, faction_name, flags
 * @returns {string | null} hex or null if unknown → caller uses CSS fallback gray
 */
export function getFactionAccentColorForLogEntity(entity) {
  if (!entity || typeof entity !== 'object') return null;
  if (entity.isConfused === true) return null;
  if (entity.isMission5Corn === true) return '#0A1F4D';
  if (entity.isExploreUnknownMemory === true) return '#1a0d2e';
  const f = entity.faction || entity.faction_name;
  if (!f || typeof f !== 'string' || !f.trim()) return null;
  const key = f.trim();
  if (isCombinedStrategyFactionName(key)) return FACTION_SLAB_COLORS.Combined;
  return FACTION_SLAB_COLORS[key] || null;
}

function hexToRgbChannels(hex) {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.trim();
  if (h.startsWith('#')) h = h.slice(1);
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (h.length !== 6 || !/^[0-9a-fA-F]+$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

/** WCAG relative luminance for sRGB hex (0 = black, 1 = white). */
function hexRelativeLuminance(hex) {
  const ch = hexToRgbChannels(hex);
  if (!ch) return 1;
  const lin = (c) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  const r = lin(ch.r);
  const g = lin(ch.g);
  const b = lin(ch.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Below this, faction text on the dark log is hard to read — use a light chip behind it. */
const BATTLE_LOG_UNIT_CHIP_MAX_LUMINANCE = 0.22;

/**
 * Inline styles for a highlighted unit/card name in battle log tooltips / drawer.
 * Very dark accents (e.g. League of Shadows) get a warm light background so the text stays legible.
 * @param {string | null | undefined} accentHex
 * @returns {Record<string, string | number> | undefined}
 */
export function getBattleLogUnitNameHighlightStyle(accentHex) {
  if (!accentHex || typeof accentHex !== 'string') return undefined;
  const trimmed = accentHex.trim();
  if (!trimmed.startsWith('#')) {
    return { color: trimmed };
  }
  const lum = hexRelativeLuminance(trimmed);
  if (lum >= BATTLE_LOG_UNIT_CHIP_MAX_LUMINANCE) {
    return { color: trimmed };
  }
  return {
    color: trimmed,
    backgroundColor: 'rgba(242, 238, 230, 0.94)',
    padding: '0.06em 0.32em',
    borderRadius: '3px',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.2)',
    WebkitBoxDecorationBreak: 'clone',
    boxDecorationBreak: 'clone',
  };
}
